/**
 * Statistiken-Tab des Owner-Dashboards.
 *
 * Aus OwnerDashboard.js herausgelöst, damit der Statistikteil für sich lesbar
 * bleibt. Die Zeitraum- und Granularitätsauswahl steht als eine Filterzeile über
 * allen Charts und wirkt auf alle Zeitreihen darunter, damit die Zahlen
 * untereinander immer denselben Ausschnitt zeigen.
 */

import React, { useState, useMemo } from 'react';
import {
  ChartCard, SegmentedControl, LineChart, ColumnChart, DivergingColumnChart,
  ShareBar, StatTile, StatGrid, ACCENT, LIFECYCLE_COLORS, seriesColor,
} from './charts/Charts';
import './OwnerStats.css';

const nf = new Intl.NumberFormat('de-AT');
const int = (v) => nf.format(Math.round(v || 0));

const LIFECYCLE = [
  { key: 'active', label: 'Aktiv', color: LIFECYCLE_COLORS.active },
  { key: 'cancelling', label: 'Kündigungsfrist', color: LIFECYCLE_COLORS.cancelling },
  { key: 'paused', label: 'Pausiert', color: LIFECYCLE_COLORS.paused },
  { key: 'cancelled', label: 'Ex', color: LIFECYCLE_COLORS.cancelled },
  { key: 'prospect', label: 'Interessent', color: LIFECYCLE_COLORS.prospect },
  { key: 'inactive', label: 'Inaktiv', color: LIFECYCLE_COLORS.inactive },
];

const RANGE_OPTIONS = [
  { value: 12, label: '12 Monate' },
  { value: 24, label: '24 Monate' },
  { value: 0, label: 'Alles' },
];

const GRAN_OPTIONS = [
  { value: 'month', label: 'Monat' },
  { value: 'quarter', label: 'Quartal' },
];

/** Letzte n Einträge (n = 0 bedeutet: alles). */
const tail = (arr, n) => (!n || arr.length <= n ? arr : arr.slice(arr.length - n));

const OwnerStats = ({ dashboard, studios = [], formatCurrency }) => {
  const [range, setRange] = useState(12);
  const [gran, setGran] = useState('month');

  const members = dashboard?.members;
  const revenue = dashboard?.revenue;

  // --- Entwicklungsdaten je nach Granularität und Zeitraum zuschneiden ---
  const development = useMemo(() => {
    if (!members) return { rows: [], labelKey: 'month' };
    if (gran === 'quarter') {
      const rows = tail(members.quarterly_development || [], range ? Math.max(1, Math.round(range / 3)) : 0);
      return { rows, labelKey: 'quarter' };
    }
    return { rows: tail(members.development || [], range), labelKey: 'month' };
  }, [members, gran, range]);

  const categories = development.rows.map((r) => r[development.labelKey]);

  // Eine Linie je Standort – Farbe hängt am Standort, nicht an der Sortierung
  const studioSeries = useMemo(
    () => studios.map((s, i) => ({
      id: s.id,
      name: s.name,
      color: seriesColor(i),
      values: development.rows.map((r) => r[`${s.id}_active`] || 0),
    })),
    [studios, development.rows]
  );

  // Zu-/Abgänge über alle Standorte summiert
  const flows = useMemo(() => ({
    newValues: development.rows.map((r) => studios.reduce((sum, s) => sum + (r[`${s.id}_new`] || 0), 0)),
    lostValues: development.rows.map((r) => studios.reduce((sum, s) => sum + (r[`${s.id}_cancelled`] || 0), 0)),
  }), [development.rows, studios]);

  // Anfragen: Wochen passend zum gewählten Monatszeitraum
  const leads = useMemo(() => {
    const all = dashboard?.leads_weekly || [];
    return tail(all, range ? Math.round(range * 4.345) : 0);
  }, [dashboard, range]);

  // --- Kennzahlen ---
  const totalsPerPeriod = useMemo(
    () => development.rows.map((r) => studios.reduce((sum, s) => sum + (r[`${s.id}_active`] || 0), 0)),
    [development.rows, studios]
  );

  const activeNow = members ? members.total_active : 0;
  const prevTotal = totalsPerPeriod.length > 1 ? totalsPerPeriod[totalsPerPeriod.length - 2] : null;
  const lastTotal = totalsPerPeriod.length ? totalsPerPeriod[totalsPerPeriod.length - 1] : 0;
  const memberDelta = prevTotal == null ? null : lastTotal - prevTotal;

  const leadsRecent = leads.slice(-4).reduce((s, w) => s + w.count, 0);
  const leadsPrev = leads.slice(-8, -4).reduce((s, w) => s + w.count, 0);
  const leadsDelta = leads.length >= 8 ? leadsRecent - leadsPrev : null;

  if (!dashboard || !members) return null;

  const granLabel = gran === 'quarter' ? 'Quartal' : 'Monat';

  return (
    <div className="owner-section stats-section">
      <div className="stats-head">
        <div>
          <h2>Statistiken</h2>
          <p className="stats-head-sub">
            Mitglieder, Anfragen und Umsatz im Verlauf. Alle Diagramme zeigen denselben Zeitraum.
          </p>
        </div>
        <div className="stats-filters">
          <SegmentedControl options={RANGE_OPTIONS} value={range} onChange={setRange} ariaLabel="Zeitraum" />
          <SegmentedControl options={GRAN_OPTIONS} value={gran} onChange={setGran} ariaLabel="Granularität" />
        </div>
      </div>

      {/* Kennzahlen */}
      <StatGrid>
        <StatTile
          hero
          label="Aktive Mitglieder"
          value={int(activeNow)}
          trend={totalsPerPeriod.slice(-12)}
          trendColor={ACCENT}
          delta={memberDelta == null ? null : { value: memberDelta, text: int(Math.abs(memberDelta)), label: `zum Vor${granLabel.toLowerCase()}`, goodWhen: 'up' }}
        />
        <StatTile
          label="Neuzugänge (30 Tage)"
          value={int(members.new_30_days)}
          tone="good"
          sub={`${int(members.new_90_days)} in 90 Tagen`}
        />
        <StatTile
          label="Kündigungen (30 Tage)"
          value={int(members.cancellations_30_days)}
          tone={members.cancellations_30_days > 0 ? 'bad' : 'neutral'}
          sub={`Netto ${members.new_30_days - members.cancellations_30_days >= 0 ? '+' : ''}${int(members.new_30_days - members.cancellations_30_days)}`}
        />
        <StatTile label="Retention-Rate" value={`${int(members.retention_rate)} %`} sub="letzte 12 Monate" />
        <StatTile
          label="Anfragen (4 Wochen)"
          value={int(leadsRecent)}
          trend={leads.slice(-12).map((w) => w.count)}
          trendColor={ACCENT}
          delta={leadsDelta == null ? null : { value: leadsDelta, text: int(Math.abs(leadsDelta)), label: 'zu den 4 Wochen davor', goodWhen: 'up' }}
        />
        {dashboard.leads && dashboard.leads.total > 0 && (
          <StatTile
            label="Aus Anfragen wurde Mitglied"
            value={`${Math.round((dashboard.leads.converted / dashboard.leads.total) * 100)} %`}
            sub={`${int(dashboard.leads.converted)} von ${int(dashboard.leads.total)} Anfragen`}
          />
        )}
        {revenue && (
          <StatTile
            label="Monatsumsatz"
            value={formatCurrency ? formatCurrency(revenue.monthly_total) : int(revenue.monthly_total)}
            sub={`Ø ${formatCurrency ? formatCurrency(revenue.avg_per_customer) : int(revenue.avg_per_customer)} pro Mitglied`}
          />
        )}
      </StatGrid>

      {/* Mitgliederentwicklung */}
      <ChartCard
        title="Mitgliederentwicklung"
        subtitle={`Aktive Mitglieder je Standort, Stand ${granLabel}sende`}
        table={{
          columns: [granLabel, ...studios.map((s) => s.name), 'Gesamt'],
          rows: development.rows.map((r, i) => [
            r[development.labelKey],
            ...studios.map((s) => int(r[`${s.id}_active`] || 0)),
            int(totalsPerPeriod[i]),
          ]),
        }}
      >
        <LineChart
          categories={categories}
          series={studioSeries}
          height={280}
          formatValue={int}
          ariaLabel="Aktive Mitglieder je Standort im Zeitverlauf"
        />
      </ChartCard>

      <div className="viz-grid cols-2">
        {/* Zu-/Abgänge */}
        <ChartCard
          title="Zu- und Abgänge"
          subtitle="Alle Standorte zusammen, oberhalb der Linie Zugänge"
          table={{
            columns: [granLabel, 'Zugänge', 'Abgänge', 'Netto'],
            rows: development.rows.map((r, i) => [
              r[development.labelKey],
              int(flows.newValues[i]),
              int(flows.lostValues[i]),
              `${flows.newValues[i] - flows.lostValues[i] >= 0 ? '+' : '−'}${int(Math.abs(flows.newValues[i] - flows.lostValues[i]))}`,
            ]),
          }}
        >
          <DivergingColumnChart
            categories={categories}
            positive={{ name: 'Zugänge', values: flows.newValues }}
            negative={{ name: 'Abgänge', values: flows.lostValues }}
            height={230}
            formatValue={int}
            ariaLabel="Zu- und Abgänge je Periode"
          />
        </ChartCard>

        {/* Anfragen */}
        <ChartCard
          title="Anfragen pro Woche"
          subtitle={
            dashboard.leads?.spam_filtered
              ? `Kontaktanfragen über das Web-Formular, ohne ${int(dashboard.leads.spam_filtered)} Spam-Einträge`
              : 'Kontaktanfragen über das Web-Formular'
          }
          table={{
            columns: ['Woche', 'Anfragen', 'davon Mitglied'],
            rows: leads.map((w) => [w.week, int(w.count), int(w.converted)]),
          }}
        >
          <ColumnChart
            categories={leads.map((w) => {
              // Achse braucht "19/26", der Tooltip trägt die volle Kalenderwoche
              const [, num, year] = w.week.match(/KW (\d+) (\d+)/) || [];
              return num ? `${num}/${year.slice(-2)}` : w.week;
            })}
            tips={leads.map((w) => w.week)}
            series={[{ id: 'leads', name: 'Anfragen', color: ACCENT, values: leads.map((w) => w.count) }]}
            height={230}
            formatValue={int}
            ariaLabel="Anfragen pro Kalenderwoche"
          />
        </ChartCard>
      </div>

      {/* Standorte */}
      <h3 className="stats-subhead">Standorte im Detail</h3>
      <div className="viz-grid cols-2">
        {members.per_studio.map((s) => {
          const sd = s.status_distribution || {};
          const book = dashboard.bookings?.per_studio?.find((b) => b.studio_id === s.studio_id);
          const segments = LIFECYCLE.map((l) => ({ label: l.label, value: sd[l.key] || 0, color: l.color }));
          const totalMembers = segments.reduce((sum, x) => sum + x.value, 0);

          return (
            <ChartCard
              key={s.studio_id}
              title={s.studio_name}
              subtitle={`${int((sd.active || 0) + (sd.cancelling || 0))} aktive von ${int(totalMembers)} erfassten Mitgliedern`}
              className="studio-card"
            >
              <div className="studio-card-stats">
                <div className="studio-card-stat">
                  <span className="studio-card-stat-value good">+{int(s.new_30_days)}</span>
                  <span className="studio-card-stat-label">Neu (30T)</span>
                </div>
                <div className="studio-card-stat">
                  <span className="studio-card-stat-value bad">−{int(s.cancellations_30_days)}</span>
                  <span className="studio-card-stat-label">Kündigungen (30T)</span>
                </div>
                <div className="studio-card-stat">
                  <span className="studio-card-stat-value">{int(s.retention_rate)} %</span>
                  <span className="studio-card-stat-label">Retention</span>
                </div>
                <div className="studio-card-stat">
                  <span className="studio-card-stat-value">{int(book?.upcoming)}</span>
                  <span className="studio-card-stat-label">Offene Buchungen</span>
                </div>
              </div>
              <ShareBar segments={segments} formatValue={int} />
            </ChartCard>
          );
        })}
      </div>
    </div>
  );
};

export default OwnerStats;
